import * as http from 'utils/http';
import { delay } from 'redux-saga';
import { put, call, takeEvery, takeLatest, select, cancelled } from 'redux-saga/effects';
import serialize from 'utils/serialize';
import config from 'config';

import {
  request,
  done,
  cancel,
  requestMore,
  cancelMore,
  doneMore,
  setSelected,
  cleanSelected,
  setOpened,
  editRequest,
  editDone,
  setFilters,
  EDIT_DONE,
} from './duck';

import { geOutcodeNextURL, getOutcodesFilters } from './selectors';

// @effect Action
const load$ = 'outcodes/load$';
const loadMore$ = 'outcodes/loadMore$';
const select$ = 'outcodes/select$';
const openModal$ = 'outcodes/openModal$';
const closeModal$ = 'outcodes/closeModal$';
const edit$ = 'outcodes/edit$';

// @effect Creators
export const loadOutcodes = (filters) => ({
  type: load$,
  payload: filters,
});

export const loadMoreOutcodes = () => ({
  type: loadMore$,
});

export const selectOutcode = (outcodes) => ({
  type: select$,
  outcodes,
});

export const opendOutcodeModal = () => ({
  type: openModal$,
  opened: true,
});

export const closeOutcodeModal = () => ({
  type: closeModal$,
  opened: false,
});

export const editOutcodes = ({ outcodes, enabled, area }) => ({
  type: edit$,
  outcodes,
  enabled,
  area,
});

// Helpers
const cleanFilters = (filters) =>
  Object.keys(filters).reduce((acc, filter) => {
    if (typeof filters[filter] !== 'boolean' && !filters[filter]) {
      return acc;
    }
    return {
      ...acc,
      [filter]: filters[filter],
    };
  }, {});

const getFilters = ({ area, pageSize, search, country, open, close, notAdded }) => {
  const filters = {
    area,
    page_size: pageSize,
    search,
    country,
    enabled: open === close ? undefined : open || !close,
    not_added: notAdded || undefined,
  };

  return cleanFilters(filters);
};

// Workers
const fetchOutcodes = ({ request, done, cancel, url }) =>
  function* (query) {
    yield put(request());
    try {
      const getURL = url || `${config.API_URL}outcodes/?${serialize(query)}`;
      const outcodes = yield call(http.get, getURL);
      yield put(done(outcodes));
    } catch (err) {
      yield put(cancel(err));
    } finally {
      if (yield cancelled()) {
        yield put(cancel(null));
      }
    }
  };

function* load({ payload }) {
  const pageSize = yield select((state) => state.outcodes.pageSize);
  const filters = getFilters({
    ...payload,
    pageSize,
  });

  yield call(fetchOutcodes({ request, done, cancel }), filters);
}

function* delayLoad(payload) {
  yield call(delay, 500);
  yield call(load, payload);
}

function* loadMore() {
  const url = yield select(geOutcodeNextURL);
  yield call(
    fetchOutcodes({
      url,
      request: requestMore,
      done: doneMore,
      cancel: cancelMore,
    })
  );
}

function* _select({ outcodes = [] }) {
  const _outcodes = typeof outcodes === 'string' ? [outcodes] : outcodes;
  yield put(setSelected(_outcodes));
}

function* modalStatus({ opened }) {
  yield put(setOpened(opened));
}

function* edit({ outcodes = [], enabled, area }) {
  yield put(editRequest());

  for (const outcode of outcodes) {
    try {
      yield call(http.patch, `${config.API_URL}outcodes/${outcode}/`, {
        enabled: [enabled],
        area: [area],
      });
    } catch (e) {
      console.error(e);
    }
  }
  yield put(closeOutcodeModal());
  yield put(editDone());
}

function* filter({ payload }) {
  yield put(setFilters(payload));
}

function* refresh() {
  const filters = yield select(getOutcodesFilters);
  yield call(load, { payload: filters });
  yield put(cleanSelected([]));
}

// Watchers
export function* watchOutcodes() {
  yield takeLatest(load$, delayLoad);
  yield takeEvery(loadMore$, loadMore);

  yield takeEvery(load$, filter);
  yield takeEvery(select$, _select);
  yield takeEvery(openModal$, modalStatus);
  yield takeEvery(closeModal$, modalStatus);
  yield takeEvery(edit$, edit);
  yield takeEvery(EDIT_DONE, refresh);
}
